import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ButtonShape, ButtonSize, ButtonVariant, Icon as IconCotopaxi, IconColor, IconName, IconSize, ProductTile, TextColor, TextSize, TextType, useDebounce, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { LazyLoadOptions, ProductTypes, } from '@as-react/common-types';
import { isOneSizeProduct, isOnlyOneSizeProduct, setTooltip, showPriceDiscount } from '@as-react/common-utils';
import { InfoIcon } from '@yonderland/andes-icons';
import { useIsClient } from '@yonderland/andes-react';
import { Price, TextAlpha, Box, Icon, Tooltip, ConditionalWrapper, useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Stack, } from '@yonderland/andes-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DeliveryPromiseMessage from '../../../components/product-detail/delivery-promise/DeliveryPromiseMessage';
import { DELAY } from '../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ProductImageHeight, ProductImageWidth } from '../../../constants/ProductConstants';
import { useElementIsVisible } from '../../../hooks/useElementIsVisible';
import { usePriceProps } from '../../../hooks/usePriceProps';
import { useZitcha } from '../../../hooks/useZitcha';
import { makeGetIsCompareProduct } from '../../../redux/compare/selectors/CompareSelectors';
import { makeIsProductInWishList } from '../../../redux/wishList/selectors/WishListSelectors';
import { getDeliveryExplanation } from '../../../util/DeliveryPromiseUtil';
import { showMobileHeader } from '../../../util/HtmlElementUtil';
import { getImageLargeUrlByProductCode, getImageTileUrlByProductCode } from '../../../util/ImageUtil';
import { getAvailableSizes, getProductDetailUrl, isExtensiveProductTile, isProductOutOfStock, } from '../../../util/ProductUtil';
import { isTouchScreen } from '../../../util/ScreenWidthUtil';
import LabelsOverlay from '../../common/labels-overlay/LabelsOverlay';
import { ProductTileSizeSelect } from '../../product-tile-size-select/ProductTileSizeSelect';
import { ProductTileExtensiveBlock, } from '../extensive-block/ProductTileExtensiveBlock';
import { ProductTileImageIcon } from './partials/ProductTileImageIcon';
import { ProductTileImageOverlay } from './partials/ProductTileImageOverlay';
import { ProductTileSizesInfo } from './partials/ProductTileSizesInfo';
import { ProductTileSwatches } from './partials/ProductTileSwatches';
export const ProductTileVertical = ({ product, format = 'normal', productTileExtensiveVariant, onTileClick, updateCompareList, onAddToBasketCallback, onWishListToggle, shouldLazyLoadImage, showSwatchesOnHover = true, showComparison = true, showComparisonOnAllPages, alwaysShowSwatches = false, iconName, onIconClick, addToBasket, getDeliveryPromise, handleSizeSelect, deliveryPromiseExplanations, singleActiveSizeFilterKeyName, scrollableWrapperSelector, hideBasketPopup, compareItems, clickAndCollectOnly, inStoreOnly, initialActiveSku, additionalQueryParameters, showSizesOnHover = false, outOfStockActions, compact, showProductOutOfStockOverlay, showColorOutOfStockOverlay, dropdownProps, inWishLister = false, outOfStockMessage, parentId, recommendedProduct, showSwatchesOnDesktop, showSwatchesOnMobile, showCompareForExtensiveTile, defaultSize, description, adjustDescriptionHeight, hideTags, customTags, addDataMonetateProductId, enablePriceReference = false, position, }) => {
    var _a, _b, _c;
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const { oneSizeProductCode, pageInfo, root, isShopLanding, feature_toggles, productImageConfig, canonicalUrl } = useAemConfig();
    const { isBrandAsCategoryPage, isBrandPage, isCategoryPage, isPDPPage, isSearchPage, isWishlistPage, isListerPage } = pageInfo;
    const { product_gender_icon, product_new_label, product_online_exclusive, product_compare } = feature_toggles;
    const generateProductImageUrl = (imageFilename) => (imageFilename &&
        getImageTileUrlByProductCode(product.productCode, imageFilename, productImageConfig.cdnDomains)) ||
        '';
    const generateProductImageUrlOldDesign = (imageFilename) => (imageFilename &&
        getImageLargeUrlByProductCode(product.productCode, imageFilename, productImageConfig.cdnDomains)) ||
        '';
    const searchColourById = (colourId) => product.colours.find(colour => colour.colourId === colourId);
    const getDefaultSize = (color) => {
        const activeSizes = getAvailableSizes(color.sizes);
        return activeSizes.length === 1
            ? activeSizes[0]
            : defaultSize
                ? activeSizes.find(size => size.sku === defaultSize)
                : null;
    };
    const [buttonState, setButtonState] = useState('interactive');
    const [activeColour, setActiveColour] = useState(searchColourById(product.colourId));
    const [image, setImage] = useState(generateProductImageUrl(activeColour.image));
    const [hoverImage, setHoverImage] = useState(generateProductImageUrl(activeColour.hoverImage));
    const [showImageHover, setShowImageHover] = useState(false);
    const [activeSize, setActiveSize] = useState(getDefaultSize(activeColour));
    const [hasSizeSelectError, setHasSizeSelectError] = useState(false);
    const swatchesRef = useRef();
    const sponsoredRef = useRef(null);
    const isVisible = useElementIsVisible(sponsoredRef, { threshold: 0.01 });
    const collectData = {
        adFormat: 'native_product',
        adId: (_a = product.adId) !== null && _a !== void 0 ? _a : '',
        placementId: '',
    };
    const productTileId = useId();
    const isMobile = useMediaQueryMatchesOnMobile();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isTouch = isTouchScreen();
    const isClient = useIsClient();
    const { collectClick, collectImpression } = useZitcha();
    const withCloseButton = iconName === IconName.CLOSE;
    const isExtensiveTile = isExtensiveProductTile(format);
    const isNormalTile = format === 'normal';
    const isEVoucherPaperVoucherOrGiftCard = product.type === ProductTypes.E_VOUCHER ||
        product.type === ProductTypes.GIFT_CARD ||
        product.type === ProductTypes.PAPER_VOUCHER;
    const isShowCompare = showComparison &&
        (isNormalTile || (showCompareForExtensiveTile && isExtensiveTile)) &&
        !isEVoucherPaperVoucherOrGiftCard &&
        (showComparisonOnAllPages ||
            isBrandAsCategoryPage ||
            isBrandPage ||
            isCategoryPage ||
            isPDPPage ||
            isSearchPage ||
            isWishlistPage);
    const isLister = isListerPage || isCategoryPage || isSearchPage;
    const productOutOfStock = isProductOutOfStock(product);
    const colorOutOfStock = activeColour.availability < 1;
    const oneSizeProduct = isOneSizeProduct(activeColour.sizes, oneSizeProductCode);
    const hideSizes = oneSizeProduct || isOnlyOneSizeProduct(activeColour.sizes) || isEVoucherPaperVoucherOrGiftCard;
    const isCompareProduct = useSelector(makeGetIsCompareProduct(product.productCode, activeColour.colourId));
    const isWishlistProduct = useSelector(makeIsProductInWishList(product.productCode, activeColour.colourId));
    const deliveryPromise = (_b = product.deliveryPromise) === null || _b === void 0 ? void 0 : _b.deliveryPromises[0];
    const deliveryPromiseExplanation = getDeliveryExplanation(product.deliveryPromise, deliveryPromiseExplanations);
    const availableProductColours = useMemo(() => {
        return isWishlistPage
            ? product.colours.filter(colour => colour.availability > 0 || colour.colourId === product.colourId)
            : product.colours;
    }, [product]);
    const coloursAmount = availableProductColours.length;
    const productPriceProps = usePriceProps({
        priceInformation: {
            prices: (activeSize === null || activeSize === void 0 ? void 0 : activeSize.prices) || activeColour.prices,
            hasDiscount: activeColour.hasDiscount,
        },
        enablePriceReference,
    });
    const savingsPercentage = showPriceDiscount(activeColour.prices, enablePriceReference)
        ? ((_c = activeColour.price) === null || _c === void 0 ? void 0 : _c.productDiscountPercentage) || 0
        : undefined;
    const wishlistEnabled = (!withCloseButton || inWishLister) && !isShopLanding;
    const isProductInWishList = isClient && isWishlistProduct;
    const contentOpacity = ((showProductOutOfStockOverlay && productOutOfStock) || (showColorOutOfStockOverlay && colorOutOfStock)) &&
        !(productOutOfStock && inWishLister)
        ? 0.5
        : undefined;
    const sizesInfo = (showSizesOnHover || alwaysShowSwatches) && !hideSizes ? _jsx(ProductTileSizesInfo, {}) : null;
    const showSwatches = Boolean(isNormalTile && ((coloursAmount > 1 && !isLister) || alwaysShowSwatches || showSwatchesOnDesktop || isLister));
    const productURL = getProductDetailUrl({
        seoUrl: product.seoUrl,
        colourId: activeColour.colourId,
        activeSize: singleActiveSizeFilterKeyName ? encodeURIComponent(singleActiveSizeFilterKeyName) : undefined,
        isRecommended: recommendedProduct,
        additionalQueryParameters,
    }, root);
    const productContext = {
        productCode: product.productCode,
        productId: product.productId,
        activeColorId: activeColour.colourId,
        brand: product.brand,
        title: product.title,
        image: image,
        hoverImage: hoverImage,
        colors: availableProductColours,
        alt: [product.brand, product.title, activeColour.description].join(' '),
        review: { amount: product.productReviews || 0, score: product.productRating || 0 },
        isSponsored: product.isSponsored || false,
        adId: product.adId || '',
    };
    const changeColor = (colour) => {
        setActiveColour(colour);
        setActiveSize(getDefaultSize(colour));
        setImage(generateProductImageUrl(colour.image));
        setHoverImage(generateProductImageUrl(colour.hoverImage));
    };
    const onSizeSelect = (skuId) => {
        setActiveSize(activeColour.sizes.find(size => size.sku === skuId));
        hasSizeSelectError && setHasSizeSelectError(false);
        handleSizeSelect === null || handleSizeSelect === void 0 ? void 0 : handleSizeSelect({ productId: product.productId, colourId: product.colourId, skuId });
    };
    const getIconName = () => {
        if (!iconName && !wishlistEnabled) {
            return null;
        }
        return iconName ? iconName : isProductInWishList ? IconName.HEART : IconName.HEART_EMPTY;
    };
    const handleImageIconClick = () => {
        wishlistEnabled
            ? onWishListToggle === null || onWishListToggle === void 0 ? void 0 : onWishListToggle({
                productCode: product.productCode,
                productId: product.productId,
                colorId: activeColour.colourId,
                sku: activeSize === null || activeSize === void 0 ? void 0 : activeSize.sku,
            })
            : onIconClick === null || onIconClick === void 0 ? void 0 : onIconClick(product.productCode, activeColour.colourId);
        !isDesktop && showMobileHeader();
    };
    const debouncedSizeSelectError = useDebounce(() => setHasSizeSelectError(false), DELAY.DELAY_3000);
    const onAddToBasketClick = () => {
        const isExtensiveBlockSimple = productTileExtensiveVariant === 'simple';
        const isTablet = !isMobile && !isDesktop;
        const isSimpleAndMobile = isMobile && isExtensiveBlockSimple;
        const isSimpleAndTablet = isTablet && isExtensiveBlockSimple;
        if (isSimpleAndMobile || isSimpleAndTablet || (activeSize === null || activeSize === void 0 ? void 0 : activeSize.sku)) {
            if (addToBasket) {
                if (!isExtensiveBlockSimple) {
                    setButtonState('loading');
                }
                addToBasket({
                    product,
                    colorId: activeColour.colourId,
                    sku: activeSize === null || activeSize === void 0 ? void 0 : activeSize.sku,
                    hideBasketPopup,
                });
                if (!isExtensiveBlockSimple) {
                    setButtonState('interactive');
                }
            }
            onAddToBasketCallback === null || onAddToBasketCallback === void 0 ? void 0 : onAddToBasketCallback();
        }
        else if (!hasSizeSelectError) {
            setHasSizeSelectError(true);
            debouncedSizeSelectError();
        }
    };
    const handleTileClick = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if ((_a = swatchesRef === null || swatchesRef === void 0 ? void 0 : swatchesRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) {
            return;
        }
        if (product.isSponsored && product.adId) {
            event.preventDefault();
            event.stopPropagation();
            yield collectClick(Object.assign(Object.assign({}, collectData), { placementId: `{"position":${position !== null && position !== void 0 ? position : 0}}` })).finally(() => {
                if (!productOutOfStock || !inWishLister) {
                    window.location.href = productURL;
                }
            });
        }
        onTileClick === null || onTileClick === void 0 ? void 0 : onTileClick(event, product, activeColour);
    });
    useEffect(() => {
        if (isExtensiveTile && activeSize && getDeliveryPromise && !activeSize.storeSourceExcluded) {
            getDeliveryPromise({
                productId: String(product.productId),
                skuId: activeSize.sku,
                colourId: product.colourId,
            });
        }
    }, [activeSize]);
    useEffect(() => {
        if (initialActiveSku) {
            setActiveSize(activeColour.sizes.find(size => size.sku === initialActiveSku));
        }
    }, [initialActiveSku]);
    useEffect(() => {
        const newActiveColour = searchColourById(activeColour.colourId);
        setActiveColour(newActiveColour);
        if (newActiveColour) {
            setImage(generateProductImageUrl(newActiveColour.image));
            setHoverImage(generateProductImageUrl(newActiveColour.hoverImage));
        }
    }, [product]);
    useEffect(() => {
        deliveryPromiseExplanation &&
            setTooltip({
                element: `#delivery-explanation-${product.productId}-${activeColour.colourId}`,
                content: deliveryPromiseExplanation,
                showCloseButton: false,
                optionsOverride: scrollableWrapperSelector
                    ? { appendTo: () => document.querySelector(scrollableWrapperSelector) }
                    : undefined,
            });
    }, [product.deliveryPromise]);
    useEffect(() => {
        if (!(productPriceProps === null || productPriceProps === void 0 ? void 0 : productPriceProps.priceActual)) {
            setButtonState('disabled');
        }
    }, []);
    useEffect(() => {
        if (!isVisible) {
            return;
        }
        collectImpression(Object.assign(Object.assign({}, collectData), { placementId: canonicalUrl !== null && canonicalUrl !== void 0 ? canonicalUrl : '' }));
    }, [isVisible]);
    return (_jsxs(ProductTile, { variant: "vertical", product: productContext, fullHeight: adjustDescriptionHeight, containerOptions: {
            dataAttributes: Object.assign({ 'data-testid': 'product-tile-vertical' }, (addDataMonetateProductId ? { 'data-monetate-product-id': `p${product.productId}` } : undefined)),
        }, children: [_jsxs(ProductTile.Area, { area: "primary", href: productOutOfStock && inWishLister ? undefined : productURL, onClick: handleTileClick, children: [product.isSponsored && product.adId && (_jsx("div", { ref: sponsoredRef, children: _jsx(Box, { paddingBlockEnd: "1", children: _jsxs(Stack, { direction: "row", align: "center", gap: "1.5", children: [_jsx(TextAlpha, { color: "subtle", variant: "bodyS", children: t('product.zitcha.sponsored.label') }), _jsx(Tooltip, { text: t('product.zitcha.sponsored.hint'), hasCloseBtn: false, children: _jsx(Icon, { source: InfoIcon, size: "xs", color: "subtle" }) })] }) }) })), _jsx(ProductTile.Image, { containerOptions: {
                            onMouseEnter: activeColour.hoverImage && !isTouch ? () => setShowImageHover(true) : undefined,
                            onMouseLeave: activeColour.hoverImage && !isTouch ? () => setShowImageHover(false) : undefined,
                        }, onImageError: () => {
                            showImageHover
                                ? setHoverImage(generateProductImageUrlOldDesign(activeColour.hoverImage))
                                : setImage(generateProductImageUrlOldDesign(activeColour.image));
                        }, onImageHoverError: activeColour.hoverImage
                            ? () => setHoverImage(generateProductImageUrlOldDesign(activeColour.hoverImage))
                            : undefined, lazyLoad: shouldLazyLoadImage !== LazyLoadOptions.NO_LAZY_LOAD, width: ProductImageWidth.LISTER, height: ProductImageHeight.LISTER, icon: _jsx(ProductTileImageIcon, { compact: compact, icon: onWishListToggle || withCloseButton ? getIconName() : null, onClick: handleImageIconClick }), overlay: ((showProductOutOfStockOverlay && productOutOfStock) ||
                            (showColorOutOfStockOverlay && colorOutOfStock)) && (_jsx(ProductTileImageOverlay, { compact: compact, text: outOfStockMessage
                                ? outOfStockMessage
                                : productOutOfStock
                                    ? t('product.temporarilyOutOfStock')
                                    : t('wishlist.outOfStock.other.color.available'), extra: outOfStockActions })) })] }), _jsx(ProductTile.Area, { area: "secondary", href: productOutOfStock && inWishLister ? undefined : productURL, onClick: handleTileClick, containerOptions: { opacity: contentOpacity }, children: _jsxs(ProductTile.Info, { expandOnHover: showSwatchesOnHover, expandOnMobile: showSwatchesOnMobile, expandOnDesktop: alwaysShowSwatches || showSwatchesOnDesktop, containerOptions: !showSwatches && !sizesInfo && isNormalTile
                        ? {
                            mobile: { spacing: { paddingBottom: 1.5 } },
                        }
                        : undefined, tags: !hideTags &&
                        (customTags || (_jsx(LabelsOverlay, { showGenderIcon: product_gender_icon, genderFeatureId: product.featureValueGenderId, savingsPercentage: savingsPercentage, priceOverlay: activeColour.priceOverlay || undefined, showNewProductLabel: product_new_label && activeColour.newProduct, showOnlineExclusiveLabel: product_online_exclusive && activeColour.extendedRange, showSustainabilityLabel: product.ourPlanet }))), swatches: showSwatches && (_jsx(ProductTileSwatches, { hidden: Boolean(alwaysShowSwatches && coloursAmount === 1 && !isLister && sizesInfo), innerRef: swatchesRef, onChange: changeColor, idPrefix: productTileId, showOnMobile: showSwatchesOnMobile })), sizes: isNormalTile && sizesInfo, children: [_jsx(ProductTile.Title, { type: compact ? undefined : TextType.CONTENT, size: compact ? TextSize.SMALL : undefined, lineClamp: compact ? 2 : undefined, reserveLine: compact ? 2 : undefined, underlineOnHover: true, brandOnTheSameLine: true, containerOptions: { mobile: { spacing: { paddingTop: 2, paddingBottom: 1 } } } }), _jsx(ProductTile.Rating, { containerOptions: { mobile: { spacing: { paddingBottom: 1 } } }, amountColor: TextColor.SUBTLE, amountSize: TextSize.XSMALL }), (description || adjustDescriptionHeight) && (_jsx(Box, { flex: adjustDescriptionHeight ? { grow: 1 } : undefined, paddingBlockStart: "1", paddingBlockEnd: { base: '3', md: '4' }, children: description })), !!productPriceProps && (_jsx(ConditionalWrapper, { condition: Boolean(productPriceProps.enablePriceReference), wrapper: children => _jsx("div", { style: { height: '42px' }, children: children }), children: _jsx(Price, Object.assign({ testId: "product-tile-vertical-price" }, productPriceProps)) })), Boolean(isNormalTile && !alwaysShowSwatches) && (_jsx(Box, { paddingBlockStart: "2", children: _jsxs(TextAlpha, { variant: "bodyS", color: "subtle", children: [coloursAmount, ' ', t(coloursAmount > 1 ? 'lister.swatch.counter_plural' : 'lister.swatch.counter', {
                                        count: coloursAmount,
                                    })] }) }))] }) }), _jsxs(ProductTile.Area, { area: "tertiary", containerOptions: { opacity: contentOpacity }, children: [isExtensiveTile && (_jsx(ProductTileExtensiveBlock, { inProductTile: true, variant: productTileExtensiveVariant, url: productOutOfStock && inWishLister ? undefined : productURL, colourId: activeColour.colourId, colours: availableProductColours, deliveryPromise: activeSize && !activeSize.storeSourceExcluded && deliveryPromise && deliveryPromise.cutoff ? (_jsx(DeliveryPromiseMessage, { deliveryPromise: deliveryPromise })) : (_jsx(TextAlpha, { color: "success", as: "span", children: (deliveryPromise === null || deliveryPromise === void 0 ? void 0 : deliveryPromise.deliveryMessage) || null })), deliveryPromiseExplanationElement: deliveryPromiseExplanation ? (_jsx(IconCotopaxi, { id: `delivery-explanation-${product.productId}-${activeColour.colourId}`, size: IconSize.MINOR, color: IconColor.POSITIVE, name: IconName.INFORMATION_OUTLINE, dynamicContent: deliveryPromiseExplanation, inline: true })) : null, oneSizeProduct: oneSizeProduct, buttonState: buttonState, addToBasket: onAddToBasketClick, outOfStock: showProductOutOfStockOverlay && productOutOfStock, colorOutOfStock: showColorOutOfStockOverlay && colorOutOfStock, clickAndCollectOnly: clickAndCollectOnly, inStoreOnly: inStoreOnly, hideAddToBasketBtn: activeSize === null || activeSize === void 0 ? void 0 : activeSize.storeSourceExcluded, sizeSelect: _jsx(ProductTileSizeSelect, { productId: product.productId, colorId: activeColour.colourId, sizes: activeColour.sizes, selectedSku: activeSize === null || activeSize === void 0 ? void 0 : activeSize.sku, isOneSizeProduct: oneSizeProduct, scrollableWrapperSelector: scrollableWrapperSelector, hasError: hasSizeSelectError, dropdownProps: dropdownProps, onSizeChange: onSizeSelect }) })), Boolean(product_compare && isShowCompare && (!withCloseButton || inWishLister)) && (_jsx(ProductTile.Compare, { fullWidth: true, containerOptions: { mobile: { spacing: { paddingTop: isExtensiveTile ? 1 : 0 } } }, id: `${parentId ? `${parentId}--` : ''}compare-${product.productCode}-${activeColour.colourId}`, label: isMobile && ((compareItems === null || compareItems === void 0 ? void 0 : compareItems.length) || 0) > 1 && isCompareProduct ? (_jsx(Button, { text: t('productCompare.pdp.checkbox'), variant: ButtonVariant.OUTLINED_ALT, size: ButtonSize.XSMALL, shape: ButtonShape.PILL, iconRight: {
                                name: IconName.CHEVRON_RIGHT,
                                size: IconSize.MEDIUM,
                            }, href: `${root}products/product-compare.html` })) : (t('productCompare.pdp.checkbox')), checked: isCompareProduct, onChange: () => updateCompareList === null || updateCompareList === void 0 ? void 0 : updateCompareList({
                            brand: product.brand,
                            color: activeColour.colourId,
                            image: activeColour.image,
                            productCode: product.productCode,
                            selected: false,
                            id: product.productId,
                            title: product.title,
                            gender: product.genderCode,
                            url: productURL,
                            colorDescription: activeColour.description,
                            collapsedComparePopup: true,
                        }), disabled: productOutOfStock && inWishLister }))] })] }));
};
